import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import {
    GraphComponent,
    GraphEditorInputMode,
    GraphItemTypes,
    IEdge,
    INode,
    Point,
    TimeSpan,
    EventRecognizers,
} from '@yfiles/yfiles';
import {
    BranchRegion,
    BusinessProcessDefinition,
} from '../../models/business-process.model';
import { BranchRegionAnalyzer } from '../../analysis/branch-region-analyzer';
import { BusinessProcessPortService } from '../../services/business-process-port.service';
import { BusinessProcessEdgeService } from '../../services/business-process-edge.service';
import {
    BusinessProcessLayoutOrientation,
    BusinessProcessLayoutService,
} from '../../services/business-process-layout.service';
import { BuildModelService } from '../../../model-services/build-model.service';
import { textTip } from '../../../canvas/node-tooltip';

// below this zoom labels are too small to read (13px text is under 4px), so they aren't drawn
const MIN_LABEL_ZOOM = 0.3;

@Component({
    selector: 'app-business-process-graph',
    standalone: false,
    templateUrl: './business-process-graph.component.html',
    styleUrl: './business-process-graph.component.css',
})
export class BusinessProcessGraphComponent implements AfterViewInit, OnDestroy {
    @ViewChild('graphHost', { static: true })
    private graphHost!: ElementRef<HTMLDivElement>;
    private processValue: BusinessProcessDefinition | null = null;

    private graphComponent!: GraphComponent;
    private initialized = false;
    private destroyed = false;
    private generation = 0;
    private branchRegions: readonly BranchRegion[] = [];
    private modelsValue:
        | { _key: string; name: string; colour: string }[]
        | null = null;

    @Input({ required: true }) set models(
        v: { _key: string; name: string; colour: string }[],
    ) {
        this.modelsValue = v;
        if (this.initialized) this.scheduleReload();
    }
    get models() {
        return this.modelsValue;
    }

    @Input({ required: true }) set process(v: BusinessProcessDefinition) {
        this.processValue = v;
        if (this.initialized) this.scheduleReload();
    }
    get process() {
        return this.processValue;
    }
    @Input() heading = 'Actions model';     // not "title", which the browser would show as a tooltip over the whole graph
    @Input() subtitle = 'How work flows from step to step';
    // the direction it lays out in: a change, as when the user changes theirs, lays it out again
    @Input() set layoutOrientation(v: BusinessProcessLayoutOrientation) {
        const changed = v !== this.layoutOrientationValue;
        this.layoutOrientationValue = v;
        if (changed && this.initialized) void this.layoutGraph().then(() => this.fitGraph());
    }
    get layoutOrientation() {
        return this.layoutOrientationValue;
    }
    private layoutOrientationValue: BusinessProcessLayoutOrientation = 'top-to-bottom';

    // what to show when hovering over a step or connection; nothing is shown when it gives no text
    @Input() itemToolTip?: (item: INode | IEdge) => string | undefined;
    // a card for a node, saying what it is and its insights; the plain text above is then kept for connections
    @Input() nodeToolTip?: (node: INode) => HTMLElement | undefined;

    // emitted each time the graph has been rebuilt and laid out, so the host can analyse and navigate it
    @Output() graphBuilt = new EventEmitter<GraphComponent>();
    // a node double-clicked, as to open what it stands for
    @Output() nodeDoubleClicked = new EventEmitter<INode>();

    constructor(
        private readonly builder: BuildModelService,
        private readonly analyzer: BranchRegionAnalyzer,
        private readonly ports: BusinessProcessPortService,
        private readonly edges: BusinessProcessEdgeService,
        private readonly layouts: BusinessProcessLayoutService,
        private readonly zone: NgZone,
    ) {}
    async ngAfterViewInit() {
        this.graphComponent = new GraphComponent(this.graphHost.nativeElement);
        this.graphComponent.graph.edgeDefaults.style =
            this.edges.createEdgeStyle();
        const inputMode = new GraphEditorInputMode({
            allowCreateNode: false,
            allowCreateEdge: false,
            deletableItems: GraphItemTypes.NONE,
            allowEditLabel: false,
            // the graph is read: nothing can be dragged, bent or resized
            movableSelectedItems: GraphItemTypes.NONE,
            movableUnselectedItems: GraphItemTypes.NONE,
            showHandleItems: GraphItemTypes.NONE,
            allowCreateBend: false,
            allowReparentNodes: false,
            toolTipItems: GraphItemTypes.NODE | GraphItemTypes.EDGE,
        });
        // quick to appear and beside the pointer, as on the model canvas
        inputMode.toolTipInputMode.toolTipLocationOffset = new Point(15, 15);
        inputMode.toolTipInputMode.delay = TimeSpan.fromMilliseconds(50);
        inputMode.toolTipInputMode.duration = TimeSpan.fromSeconds(10);
        inputMode.addEventListener('query-item-tool-tip', (evt) => {
            if (evt.handled) return;
            if (evt.item instanceof INode && this.nodeToolTip) {
                const card = this.nodeToolTip(evt.item);
                if (card) {
                    evt.toolTip = card;
                    evt.handled = true;
                    return;
                }
            }
            if (!this.itemToolTip) return;
            const text = this.itemToolTip(evt.item as INode | IEdge);
            if (!text) return;
            // in an element as text: yFiles would set the string as markup
            evt.toolTip = textTip(text);
            evt.handled = true;
        });
        // the canvas pans with Alt and a drag, as the model canvas does; a plain drag selects
        inputMode.moveViewportInputMode.beginRecognizer = (evt, sender) =>
            EventRecognizers.MOUSE_LEFT_DOWN(evt, sender) && EventRecognizers.ALT_IS_DOWN(evt, sender);
        inputMode.addEventListener('item-double-clicked', (evt) => {
            if (!(evt.item instanceof INode)) return;
            const node = evt.item;
            // yFiles reports it outside Angular, which then wouldn't see what the page does with it
            this.zone.run(() => this.nodeDoubleClicked.emit(node));
        });
        this.graphComponent.inputMode = inputMode;
        // labels can't be read when zoomed well out, and drawing thousands of them makes panning slow
        this.graphComponent.addEventListener('zoom-changed', () =>
            this.showLabelsForZoom(),
        );
        this.initialized = true;
        if (this.processValue) await this.reloadProcess();
    }
    ngOnDestroy() {
        this.destroyed = true;
        this.generation++;
        this.graphComponent?.cleanUp();
    }
    // the models and the process are often set together: the graph is built once for both, not once for each
    private reloadScheduled = false;
    private scheduleReload() {
        if (this.reloadScheduled) return;
        this.reloadScheduled = true;
        queueMicrotask(() => {
            this.reloadScheduled = false;
            void this.reloadProcess();
        });
    }
    private async reloadProcess() {
        if (!this.initialized || this.destroyed || !this.processValue) return;
        const gen = ++this.generation,
            g = this.graphComponent.graph;
        g.clear();

        const build = this.builder.buildAnalysisModel(
                this.models,
                this.processValue,
                this.graphComponent,
            ),
            analysis = this.analyzer.analyze(g.nodes, build.connections);
        this.branchRegions = analysis.regions;
        const p = this.ports.configure(g, g.nodes, build.connections);
        this.edges.createEdges(
            g,
            build.connections,
            p,
            analysis.branchPathOrder,
        );
        if (gen !== this.generation || this.destroyed) return;
        await this.layouts.applyInitialLayout(
            this.graphComponent,
            analysis.regions,
            this.layoutOrientation,
        );
        if (gen === this.generation && !this.destroyed) {
            this.fitGraph();
            this.graphBuilt.emit(this.graphComponent);
        }
    }
    async layoutGraph() {
        if (this.initialized && !this.destroyed)
            await this.layouts.applyAnimatedLayout(
                this.graphComponent,
                this.branchRegions,
                this.layoutOrientation,
            );
    }
    fitGraph() {
        if (this.initialized && !this.destroyed)
            this.graphComponent.fitGraphBounds(40);
    }
    private showLabelsForZoom() {
        const visible = this.graphComponent.zoom >= MIN_LABEL_ZOOM,
            manager = this.graphComponent.graphModelManager;
        if (manager.nodeLabelGroup.visible === visible) return;
        manager.nodeLabelGroup.visible = visible;
        manager.edgeLabelGroup.visible = visible;
    }
}
